import styled from 'styled-components';
import { ShadowBoxDark } from 'app/components';

export const SectionEntities = styled.section.attrs({
  className: 's-entities'
})`
  flex: 1 0 auto;
  padding: 0 0 48px;

  @media screen and (max-width: ${({ theme }) => theme.breakPoints.md.width}) {
    padding: ${({ noPadding }) => (noPadding ? '0' : '47px')} 0 48px;
  }

  @media screen and (max-width: ${({ theme }) => theme.breakPoints.xs.width}) {
    padding: ${({ noPadding }) => (noPadding ? '0' : '45px')} 0 48px;
  }
`;

export const SectionEntityDetails = styled.section.attrs({
  className: 's-entity-details'
})`
  flex: 1 0 auto;
  padding: ${({ theme }) => theme.indent.m} 0 48px;

  .s-entity-details__nav-tabs {
    display: none;
    width: 100%;
    margin-bottom: ${({ theme }) => theme.indent.m};
    font-size: 0;
  }

  & > .container {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
  }

  @media screen and (max-width: ${({ theme }) => theme.breakPoints.md.width}) {
    padding: 0 0 48px;
    overflow: hidden;

    .s-entity-details__nav-tabs {
      display: block;
    }
  }
`;

export const MainEntityDetails = styled.main.attrs({
  className: 's-entity-details__main'
})`
  width: calc(100% - 329px);
  padding-right: ${({ theme }) => theme.indent.xl};

  .b-no-content + .b-entity-content,
  .b-entity-gallery + .b-entity-content,
  .b-entity-gallery + .b-no-content,
  .b-entity-gallery + .b-cinema-info,
  .b-entity-gallery + .l-sessions,
  .b-title-with-poster + .b-entity-content,
  .b-title-with-poster + .b-no-content,
  .b-entity-gallery + .b-shadows-box-dark,
  .b-entity-gallery + .b-session-state,
  .b-session-state + .l-sessions,
  .b-session-state + .b-no-content {
    margin-top: ${({ theme }) => theme.indent.l};
  }

  .b-entity-content + .b-cinema-info {
    margin-top: ${({ theme }) => theme.indent.s};
  }

  .b-entity-content .l-entity {
    margin-top: ${({ theme }) => theme.indent.m};
  }

  .b-session-state + .b-entity-content {
    margin-top: ${({ theme }) => theme.indent.l};
  }

  @media screen and (max-width: ${({ theme }) => theme.breakPoints.md.width}) {
    display: ${({ isVisible }) => (isVisible ? `block` : `none`)};
    width: 100%;
    padding-right: 0;

    .b-no-content + .b-entity-content {
      margin-top: ${({ theme }) => theme.indent.m};
    }
  }
`;

export const AsideEntityDetails = styled(ShadowBoxDark).attrs({
  className: 's-entity-details__aside'
})`
  width: 329px;
  overflow: hidden;

  .s-entity-details__aside-inner {
    padding: ${({ theme }) => `12px ${theme.indent.m} ${theme.indent.s}`};
  }

  @media screen and (max-width: ${({ theme }) => theme.breakPoints.md.width}) {
    display: ${({ isVisible }) => (isVisible ? `block` : `none`)};
    width: 100%;
  }
`;

export const EntityInfo = styled.div.attrs({
  className: 'b-entity-info'
})`
  .b-entity-info__row {
    &.b-entity-info__age {
      position: relative;
    }

    & > strong {
      display: inline-block;
      margin-right: ${({ theme }) => theme.indent.s};
      font-size: 1.6rem;
      font-weight: 500;
      line-height: 1.5;
      color: ${({ theme }) => theme.color.labelColor};

      &._block {
        display: block;
        margin-right: 0;
      }
    }
  }

  .b-entity-info__age-text {
    width: 100%;
    max-width: 295px;
    padding: ${({ theme }) => theme.indent.m};
    border-radius: 8px;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.61), 0 0 4px 0 rgba(0, 0, 0, 0.6);
    background-color: ${({ theme }) => theme.color.bgSecondary};
    position: absolute;
    top: calc(100% + 3px);
    left: 50%;
    z-index: 1;
    transform: translateX(-50%) scale(0.5);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.18s ease, transform 0.2s ease-out;
  }

  .b-entity-info__age-mark {
    display: inline-block;
    vertical-align: sub;
    margin-left: ${({ theme }) => theme.indent.s};
    width: 18px;
    height: 18px;

    &:hover {
      .svg-question-mark {
        opacity: 1;
      }

      & + .b-entity-info__age-text {
        opacity: 1;
        transform: translateX(-50%) scale(1);
      }
    }

    .svg-question-mark {
      width: 100%;
      height: 100%;
      fill: ${({ theme }) => theme.color.textPrimary};
      opacity: 0.8;
      transition: opacity 0.2s ease-out;
    }
  }

  .b-entity-info__row + .b-entity-info__row {
    margin-top: 9px;
  }
`;

export const EntityContent = styled(ShadowBoxDark).attrs({
  className: 'b-entity-content'
})`
  padding: ${({ theme }) => `${theme.indent.m} ${theme.indent.m} 18px`};

  & ~ .b-entity-content {
    margin-top: ${({ theme }) => theme.indent.l};
  }

  .b-entity-content__link-wrapper {
    position: relative;
    padding-left: 30px;

    & > button {
      position: absolute;
      left: 0;
    }
  }

  .b-entity-content__text {
    margin-top: ${({ theme }) => theme.indent.s};
  }

  .b-entity-content__text > ol > li {
    display: list-item;
    list-style: auto;
  }

  .b-entity-content__text > ul {
    display: block;
    margin-block-start: 1em;
    margin-block-end: 1em;
    margin-inline-start: 0px;
    margin-inline-end: 0px;
    padding-inline-start: 40px;
  }

  .b-entity-content__text > ul > li {
    display: list-item;
    list-style: disc;
  }

  .b-entity-content__title {
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 1.44;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: ${({ theme }) => theme.color.accent};
  }

  .b-entity-content__link {
    transition: color 0.2s ease-out;
    backface-visibility: hidden;

    &:hover {
      color: ${({ theme }) => theme.color.textPrimary};
    }
  }

  @media screen and (max-width: ${({ theme }) => theme.breakPoints.md.width}) {
    & ~ .b-entity-content {
      margin-top: ${({ theme }) => theme.indent.l};
    }
  }
`;
